import HCaptcha from '@hcaptcha/react-hcaptcha';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';

import { newsletterApi } from '../../helpers/api/newsletter-api';
import { isBrowser } from '../../helpers/is-browser';
import { HttpStatusCodes, setLocalStorageItem } from '../../helpers/utils';
import { FormState, useForm } from '../../hooks/use-form';
import useGetMobileAppWebViewHeader from '../../hooks/use-get-mobile-app-web-view';
import {
  INewsletterFormData,
  useGetNewsletterFormData,
} from '../../hooks/use-get-newsletter-form-data';
import { useSendGtmEvent } from '../../hooks/use-send-gtm-event';
import { RichText } from '../rich-text/rich-text';
import Button from '../ui/button/button';
import { Checkbox } from '../ui/checkbox/checkbox';
import InputField from '../ui/input-field/input-field';
import LinkWrapper from '../ui/link-wrapper/link-wrapper';
import Spinner from '../ui/spinner/spinner';

export interface NewsletterProps {
  handleClose?: () => void;
  locale: string;
  integrationId: string;
  businessUnitCode: string;
  variant?: 'campaign';
  onSubscribeClick?: () => void;
}

interface IParams {
  email: string;
  subscribe: boolean;
}

const NewLetterForm = (formProps: NewsletterProps) => {
  const isWebView = useGetMobileAppWebViewHeader();

  // fetch Form Data
  const formData: INewsletterFormData = useGetNewsletterFormData(
    formProps.locale,
  );

  // Set up form
  const initalFormState: FormState = {
    name: { value: '', required: true, error: false, maxLength: 50 },
    email: { value: '', required: true, error: false, maxLength: 254 },
  };

  const {
    onChange,
    values,
    updateInputValidityOnBlurOrFocus,
    setValues,
    updateFormValidity,
  } = useForm(initalFormState);

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [token, setToken] = useState<null | string>(null);
  const [queryParams, setqueryParams] = useState<IParams | null>(null);
  const [unSubscribe, setUnsubscribe] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [subscribeError, setSubscribeError] = useState<boolean>(false);
  const [unSubscribeError, setUnSubscribeError] = useState<boolean>(false);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);

  // Refs
  const messageRef = useRef<null | HTMLDivElement>(null);
  const captchaRef = useRef<HCaptcha>(null);

  //Checkbox
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const onCheckboxChange = () => {
    setAgreeToTerms(!agreeToTerms);
  };

  const isCampaignPage = formProps?.variant === 'campaign';

  // check for email and subscribe params in the url
  useEffect(() => {
    const windowUrl = window.location.search;
    const params = new URLSearchParams(windowUrl);
    const subscribe = params.get('subscribe');
    const email = params.get('EmailId');
    if (email && subscribe === 'false') {
      setqueryParams({
        ...queryParams,
        email,
        subscribe: subscribe === 'false' ? false : true,
      });
    }
  }, []);

  // unsubscribe on page load if params exist
  useEffect(() => {
    if (queryParams?.email) {
      unsubscribeNewletter();
    }
  }, [queryParams]);

  // show captcha only if the user tries subscribing more than 2 times within 10 mins
  useEffect(() => {
    const count = localStorage.getItem('count');
    const timeStamp = localStorage.getItem('timeStamp');

    if (count && timeStamp) {
      const timeDiffInMinutes =
        DateTime.fromISO(timeStamp).diffNow('minutes').minutes;

      if (Math.abs(timeDiffInMinutes) < 10 && +count >= 2) {
        setShowCaptcha(true);
      } else if (Math.abs(timeDiffInMinutes) > 10) {
        resetBrowserStorage();
      }
    }
  }, []);

  useEffect(() => {
    //Submit button disabled unless user agrees to terms or all fields are valid
    const disabled: boolean[] = [!agreeToTerms];
    for (const field of Object.values(values)) {
      disabled.push(field.error || (field.required && field.value === ''));
    }
    setSubmitButtonDisabled(disabled.includes(true));
    if (showCaptcha && !token) setSubmitButtonDisabled(true);
  }, [setSubmitButtonDisabled, values, agreeToTerms, token]);

  //On submit form or show error, scroll to top in order to show entire message
  useEffect(() => {
    if (formSubmitted && isBrowser && messageRef && messageRef.current) {
      messageRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [formSubmitted, messageRef]);

  if (!formData) {
    return null;
  }

  // Clear all fields
  const clearAllFields = () => {
    setValues(initalFormState);
    setAgreeToTerms(false);
    if (token) {
      setToken(null);
      captchaRef.current?.resetCaptcha();
    }
  };

  const sendEventToDataLayer = (interaction_type: string) => {
    if (isBrowser && dataLayer) {
      dataLayer.push({
        event: 'newsletter_form_subscription',
        user_subscribed: interaction_type,
        campaign_page: isCampaignPage ? 'Yes' : 'No',
      });
    }
  };

  // on submit
  const onSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      updateFormValidity(event.currentTarget);
      return;
    }
    setShowSpinner(true);

    const payload = {
      firstName: values.name.value.trim(),
      lastName: '',
      email: values.email.value,
      language: formProps.locale,
      meetingPlaceId: formProps.businessUnitCode,
      sourceSystemId: isWebView
        ? formProps.businessUnitCode + '_APP'
        : formProps.businessUnitCode + '_WEBSITE',
      subscriptionId: formProps.businessUnitCode + '001',
      subscriptionConsent: '',
      termsConsent: {
        id: 'terms.' + formProps.businessUnitCode,
        consent: true,
      },
      privacyConsent: {
        id: 'privacy.' + formProps.businessUnitCode,
        consent: true,
      },
      marketingConsent: { id: 'marketing.email', consent: true },
    };

    try {
      payload.subscriptionConsent = 'Y';
      const response = await newsletterApi(payload, formProps.integrationId);
      if (formProps.onSubscribeClick) formProps.onSubscribeClick();

      if (response.status_code === 200) {
        sendEventToDataLayer('Yes');
        const count = localStorage.getItem('count');
        const timeStamp = localStorage.getItem('timeStamp');
        const currentTime = DateTime.now().toISO();

        if (currentTime !== null) {
          if (count !== null && timeStamp !== null) {
            const newCount = +count + 1;
            updateLocalStorage(newCount.toString(), currentTime);
          } else {
            initializeLocalStorage('1', currentTime);
          }
        } else {
          // Handle the case where currentTime is null
          // eslint-disable-next-line no-console
          console.error('Error: Unable to get current time.');
        }
        setFormSubmitted(true);
        setLocalStorageItem('hasSubscribedToNewsletter', 'true');
      } else if (response.status_code === HttpStatusCodes.CONFLICT) {
        setShowEmailError(true);
        setLocalStorageItem('hasSubscribedToNewsletter', 'true');
      }
    } catch (e) {
      setSubscribeError(true);
    }
    setShowSpinner(false);
  };

  function initializeLocalStorage(countValue: string, timeStampValue: string) {
    localStorage.setItem('count', countValue);
    localStorage.setItem('timeStamp', timeStampValue);
  }

  function updateLocalStorage(countValue: string, timeStampValue: string) {
    localStorage.setItem('count', countValue);
    localStorage.setItem('timeStamp', timeStampValue);
  }

  const resetBrowserStorage = () => {
    localStorage.removeItem('count');
    localStorage.removeItem('timeStamp');
  };

  // unsubscribe
  const unsubscribeNewletter = async () => {
    const payload = {
      email: queryParams?.email || '',
      language: formProps.locale,
      meetingPlaceId: formProps.businessUnitCode,
      sourceSystemId: isWebView
        ? formProps.businessUnitCode + '_APP'
        : formProps.businessUnitCode + '_WEBSITE',
      subscriptionId: formProps.businessUnitCode + '001',
      subscriptionConsent: '',
      termsConsent: {
        id: 'terms.' + formProps.businessUnitCode,
        consent: true,
      },
      privacyConsent: {
        id: 'privacy.' + formProps.businessUnitCode,
        consent: true,
      },
      marketingConsent: { id: 'marketing.email', consent: true },
    };
    setShowSpinner(true);
    try {
      payload.subscriptionConsent = 'N';
      const response = await newsletterApi(payload, formProps.integrationId);
      if (response) {
        setUnsubscribe(true);
        setLocalStorageItem('hasUnSubscribedToNewsletter', 'true');
      }
    } catch (e) {
      setUnSubscribeError(true);
    }
    setShowSpinner(false);
  };

  return (
    <>
      <div
        ref={messageRef}
        className={`text-neutral-grey-700 ${isCampaignPage ? '!text-brand-white' : ''}`}
      >
        {subscribeError && (
          <>
            <h2 className="mb-5 font-title text-headingl lg:mb-8">
              {formData.additionalBlocks.subscribeErrorTitle}
            </h2>
            <RichText
              richText={formData.additionalBlocks.subscribeErrorMessage}
            />
            <Button
              onClick={() => {
                setSubscribeError(false);
                clearAllFields();
              }}
              className="mt-8 w-full"
              size="regular"
            >
              {formData.additionalBlocks.genericErrorReloadButtonLabel}
            </Button>
          </>
        )}

        {showEmailError && (
          <>
            <h2 className="mb-5 font-title text-headingl lg:mb-8">
              {formData.additionalBlocks.emailAlreadyExistTitle}
            </h2>
            <RichText
              richText={formData.additionalBlocks.emailAlreadyExistDescription}
            />
            {!isWebView && !isCampaignPage && (
              <Button
                onClick={formProps.handleClose}
                className="mt-8 w-full"
                size="regular"
              >
                {formData.additionalBlocks.closeButtonLabel}
              </Button>
            )}
          </>
        )}

        {unSubscribeError && (
          <>
            <h2 className="mb-5 font-title text-headingl lg:mb-8">
              {formData.additionalBlocks.unsubscribeErrorTitle}
            </h2>
            <RichText
              richText={formData.additionalBlocks.unsubscribeErrorMessage}
            />
            {!isWebView && (
              <Button
                onClick={formProps.handleClose}
                className="mt-8 w-full"
                size="regular"
              >
                {formData.additionalBlocks.closeButtonLabel}
              </Button>
            )}
          </>
        )}

        {unSubscribe && !unSubscribeError && (
          <>
            <h2 className="mb-5 font-title text-headingl lg:mb-8">
              {formData.additionalBlocks.unsubscribeTitle}
            </h2>
            <RichText
              richText={formData.additionalBlocks.unsubscribeDescription1}
            />
            <p className="mb-4 font-bold">{queryParams?.email}</p>
            <RichText
              richText={formData.additionalBlocks.unsubscribeDescription2}
            />
          </>
        )}

        {formSubmitted && (
          <>
            <h2
              className={`mb-4 font-title text-headingl ${isCampaignPage ? 'lg:text-headingxxl' : ''}`}
            >
              {
                formData.additionalBlocks
                  .subscriptionEmailConfirmationModalTitle
              }
            </h2>
            {formData?.additionalBlocks
              .subscriptionEmailConfirmationModalDescription && (
              <>
                <RichText
                  richText={
                    formData.additionalBlocks
                      .subscriptionEmailConfirmationModalDescription
                  }
                />
                <p className="font-bold">{values.email.value}</p>
              </>
            )}
            {!isWebView && !isCampaignPage && (
              <Button
                size="regular"
                type="submit"
                variant="primary"
                className="mt-8 w-full"
                onClick={formProps.handleClose}
              >
                {formData.additionalBlocks.closeButtonLabel}
              </Button>
            )}
          </>
        )}

        {!formSubmitted &&
          !queryParams &&
          !subscribeError &&
          !unSubscribeError &&
          !showEmailError && (
            <>
              <h2
                className={`mb-4 font-title text-headingl ${isCampaignPage ? 'hidden' : ''}`}
              >
                {formData.newsletterSubscriptionTitle}
              </h2>
              {formData.additionalBlocks.descriptionText && (
                <RichText
                  richText={formData.additionalBlocks.descriptionText}
                />
              )}
            </>
          )}
      </div>

      {!formSubmitted &&
        !queryParams &&
        !subscribeError &&
        !unSubscribeError &&
        !showEmailError && (
          <form
            noValidate
            onSubmit={onSubmit}
            className="mt-8 grid grid-cols-1 gap-4"
          >
            <InputField
              name="name"
              id="name"
              disabled={false}
              type="text"
              label={formData.additionalBlocks.nameFieldLabel}
              placeholder={formData.additionalBlocks.nameFieldPlaceholder}
              autoComplete={false}
              value={values.name.value}
              onChange={onChange}
              onBlur={updateInputValidityOnBlurOrFocus}
              required={values.name.required}
              hasError={values.name.error}
              errorMessage={formData.additionalBlocks.nameFieldErrorMessage}
              maxLength={values.name.maxLength}
              visuallyHideLabel={true}
              pattern="^[\p{L}'\- ]{1,50}$"
            />

            <InputField
              name="email"
              id="email"
              disabled={false}
              type="email"
              label={formData.additionalBlocks.emailFieldLabel}
              placeholder={formData.additionalBlocks.emailFieldPlaceholder}
              autoComplete={false}
              value={values.email.value}
              hasError={values.email.error}
              required={values.email.required}
              onChange={onChange}
              onBlur={updateInputValidityOnBlurOrFocus}
              maxLength={values.email.maxLength}
              errorMessage={formData.additionalBlocks.emailFieldErrorMessage}
              // pattern="^[\w.-]+@([\w-]+[\.])+[\w-]{2,}$"
              visuallyHideLabel={true}
            />

            {showCaptcha && (
              <div className="flex justify-center">
                <HCaptcha
                  sitekey={
                    process.env.HCAPTCHA_SITEKEY ||
                    process.env.GATSBY_HCAPTCHA_SITEKEY ||
                    ''
                  }
                  onVerify={(tocken) => setToken(tocken)}
                  ref={captchaRef}
                  onExpire={() => setToken(null)}
                  languageOverride={formProps.locale}
                />
              </div>
            )}

            <Checkbox
              id="agreeCheckbox"
              checked={agreeToTerms}
              name="termsOfUse"
              onChange={onCheckboxChange}
            >
              <span
                className={`text-bodys ${isCampaignPage ? '!text-brand-white !text-bodyl' : ''}`}
              >
                {
                  formData.termsAndConditionsCheckboxLabel
                    ?.termsAndConditionsCheckboxLabel
                }{' '}
                <LinkWrapper
                  data={formData.termsAndConditionsPageLink}
                  className={'font-bold underline'}
                  target="_blank"
                  rel="noreferrer"
                >
                  {formData.termsAndConditionsCheckboxLabelLink}
                </LinkWrapper>
              </span>
            </Checkbox>

            <div className="mt-4">
              <Button
                size="regular"
                type="submit"
                variant="primary"
                disabled={submitButtonDisabled}
                className={isCampaignPage ? '' : 'w-full'}
              >
                {formData.additionalBlocks.subscribeButtonLabel}
              </Button>
            </div>
          </form>
        )}
      <Spinner show={showSpinner} />
    </>
  );
};

export default NewLetterForm;
